import { useState, useRef, useCallback, useEffect } from 'react';
import { getIn } from 'formik';
import useAxios from './useAxios';
import Constants from '../Constants';
import { debounce } from '../utils/helper';

const useAddressSuggestions = ({ fieldPath, onAfterChange, formik } = {}) => {
    const fields = getIn(formik?.values, fieldPath) ?? Constants.EmptyAddress;

    const fieldsRef = useRef(fields);
    useEffect(() => { fieldsRef.current = fields; }, [fields]);

    const [zipError, setZipError] = useState('');
    const [streetSuggestions, setStreetSuggestions] = useState([]);

    const latestZipRef = useRef('');

    const { getData: getZipData, isLoading: zipCodeLoading } = useAxios();
    const { getData: getSuggestionData, isLoading: isSearchingStreet } = useAxios();

    const applyPatch = useCallback((patch) => {
        const next = { ...fieldsRef.current, ...patch };
        Object.entries(patch).forEach(([key, val]) => {
            formik?.setFieldValue(`${fieldPath}.${key}`, val);
        });
        onAfterChange?.(next);
    }, [formik, fieldPath, onAfterChange]);

    const fetchZipCodeDetails = useCallback(async (zip) => {
        if (zip.length !== 5) return;
        latestZipRef.current = zip;
        setZipError('');

        const res = await getZipData(`${Constants.ApiUrl.address.zipCode}?zip=${zip}`);

        if (latestZipRef.current !== zip) return;

        if (!res?.success) {
            setZipError(res?.message || 'Zip code not found');
            applyPatch({ city: '', state: '' });
            return;
        }

        applyPatch({ city: res.data.city, state: res.data.state });
    }, [applyPatch, getZipData]);

    const fetchStreetSuggestions = useCallback(async (text) => {
        if (!text.trim()) return setStreetSuggestions([]);
        const zip = fieldsRef.current.zip;
        const url = `${Constants.ApiUrl.address.suggestions}?text=${encodeURIComponent(text)}${zip ? `&zip=${zip}` : ''}`;
        const res = await getSuggestionData(url);
        if (res?.success) setStreetSuggestions(res.data);
    }, [getSuggestionData]);

    // refs keep debounced wrappers stable while always calling the latest callback versions
    const fetchStreetSuggestionsRef = useRef(fetchStreetSuggestions);
    useEffect(() => { fetchStreetSuggestionsRef.current = fetchStreetSuggestions; }, [fetchStreetSuggestions]);

    const fetchZipCodeDetailsRef = useRef(fetchZipCodeDetails);
    useEffect(() => { fetchZipCodeDetailsRef.current = fetchZipCodeDetails; }, [fetchZipCodeDetails]);

    const debouncedFetchStreet = useRef(debounce((text) => fetchStreetSuggestionsRef.current(text), 400));
    const debouncedFetchZip = useRef(debounce((zip) => fetchZipCodeDetailsRef.current(zip), 400));

    const onZipChange = useCallback((e) => {
        const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
        setZipError('');
        applyPatch({ zip });
        debouncedFetchZip.current(zip);
    }, [applyPatch]);

    const onStreetChange = useCallback((e) => {
        const address = e.target.value;
        applyPatch({ address });
        debouncedFetchStreet.current(address);
    }, [applyPatch]);

    // selecting a suggestion fills address, city, state, and zip all at once
    const onSuggestionSelect = useCallback((suggestion) => {
        applyPatch({
            address: suggestion.street || suggestion.label,
            city: suggestion.city,
            state: suggestion.state,
            zip: suggestion.zip,
        });
        setStreetSuggestions([]);
    }, [applyPatch]);

    const clearSuggestions = useCallback(() => setStreetSuggestions([]), []);

    const onFieldChange = useCallback((name) => (e) => {
        applyPatch({ [name]: e.target.value });
    }, [applyPatch]);

    const reset = useCallback(() => {
        latestZipRef.current = '';
        setZipError('');
        setStreetSuggestions([]);
        Object.entries(Constants.EmptyAddress).forEach(([key, val]) => {
            formik?.setFieldValue(`${fieldPath}.${key}`, val);
        });
    }, [formik, fieldPath]);

    return {
        fields,
        zipCodeLoading,
        isSearchingStreet,
        zipError,
        streetSuggestions,
        onZipChange,
        onStreetChange,
        onSuggestionSelect,
        clearSuggestions,
        onFieldChange,
        reset,
    };
};

export default useAddressSuggestions;
